import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { PostgrestError } from '@supabase/supabase-js';
import { useAuth } from '../contexts/AuthContext';
import { UserProfile } from '../types/database';

const normalizeProfile = (profile: UserProfile): UserProfile => ({
  ...profile,
  interests: profile.interests ?? [],
  goals: profile.goals ?? [],
  gmail_connected: profile.gmail_connected ?? false,
  user_type: profile.user_type ?? 'user', // Default to 'user' if not set
});

export interface UserProfileHook {
  profile: UserProfile | null;
  loading: boolean;
  updating: boolean;
  error: string | null;
  refreshProfile: () => Promise<UserProfile | null>;
  updateBasicInfo: (payload: { fullName?: string | null; preferredEmail?: string | null }) => Promise<UserProfile | null>;
  updateOptionalFields: (payload: { bio?: string | null; interests?: string[]; goals?: string[] }) => Promise<UserProfile | null>;
  markDataImported: () => Promise<UserProfile | null>;
  setGmailConnected: (connected: boolean) => Promise<UserProfile | null>;
}

export function useUserProfile(): UserProfileHook {
  const { user, viewingAsUserId } = useAuth();
  const supabase = useSupabaseClient();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleError = useCallback((err: PostgrestError | Error | null) => {
    if (!err) {
      setError(null);
      return;
    }

    const message = err instanceof Error ? err.message : (err as PostgrestError).message ?? 'Unknown error';
    console.error('useUserProfile error:', err);
    setError(message);
  }, []);

  const ensureProfile = useCallback(async (): Promise<UserProfile | null> => {
    const effectiveUserId = viewingAsUserId || user?.id || null;
    if (!effectiveUserId) {
      setProfile(null);
      return null;
    }

    const { data, error: fetchError } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', effectiveUserId)
      .maybeSingle();

    if (fetchError) {
      handleError(fetchError);
      return null;
    }

    if (data) {
      const normalized = normalizeProfile(data as UserProfile);
      setProfile(normalized);
      handleError(null);
      return normalized;
    }

    const { data: created, error: insertError } = await supabase
      .from('user_profiles')
      .insert({
        user_id: effectiveUserId,
        interests: [],
        goals: [],
        user_type: 'user', // Default to 'user' type for new profiles
      })
      .select('*')
      .single();

    if (insertError) {
      handleError(insertError);
      return null;
    }

    const normalizedCreated = normalizeProfile(created as UserProfile);
    setProfile(normalizedCreated);
    handleError(null);
    return normalizedCreated;
  }, [handleError, supabase, viewingAsUserId, user?.id]);

  const refreshProfile = useCallback(async () => {
    const effectiveUserId = viewingAsUserId || user?.id || null;
    if (!effectiveUserId) {
      setProfile(null);
      return null;
    }

    setLoading(true);
    try {
      return await ensureProfile();
    } finally {
      setLoading(false);
    }
  }, [ensureProfile, viewingAsUserId, user?.id]);

  const updateProfile = useCallback(
    async (updates: Record<string, any>): Promise<UserProfile | null> => {
      if (!Object.keys(updates).length) {
        return profile ?? ensureProfile();
      }

      let targetProfile = profile;
      if (!targetProfile) {
        targetProfile = await ensureProfile();
        if (!targetProfile) {
          return null;
        }
      }

      setUpdating(true);
      try {
        const { data, error: updateError } = await supabase
          .from('user_profiles')
          .update({ ...updates })
          .eq('id', targetProfile.id)
          .select('*')
          .single();

        if (updateError) {
          handleError(updateError);
          return null;
        }

        const normalized = normalizeProfile(data as UserProfile);
        setProfile(normalized);
        handleError(null);
        return normalized;
      } catch (err) {
        handleError(err instanceof Error ? err : new Error('Failed to update profile'));
        return null;
      } finally {
        setUpdating(false);
      }
    },
    [ensureProfile, handleError, profile, supabase]
  );

  const updateBasicInfo = useCallback(
    (payload: { fullName?: string | null; preferredEmail?: string | null }) => {
      const updates: Record<string, any> = {};
      if (payload.fullName !== undefined) {
        updates.full_name = payload.fullName;
      }
      if (payload.preferredEmail !== undefined) {
        updates.preferred_email = payload.preferredEmail;
      }
      return updateProfile(updates);
    },
    [updateProfile]
  );

  const updateOptionalFields = useCallback(
    (payload: { bio?: string | null; interests?: string[]; goals?: string[] }) =>
      updateProfile({
        ...(payload.bio !== undefined ? { bio: payload.bio } : {}),
        ...(payload.interests !== undefined ? { interests: payload.interests } : {}),
        ...(payload.goals !== undefined ? { goals: payload.goals } : {}),
      }),
    [updateProfile]
  );

  const markDataImported = useCallback(
    () => updateProfile({ data_imported_at: new Date().toISOString() }),
    [updateProfile]
  );

  const setGmailConnected = useCallback(
    (connected: boolean) => updateProfile({ gmail_connected: connected }),
    [updateProfile]
  );

  useEffect(() => {
    refreshProfile();
  }, [refreshProfile]);

  return useMemo(
    () => ({
      profile,
      loading,
      updating,
      error,
      refreshProfile,
      updateBasicInfo,
      updateOptionalFields,
      markDataImported,
      setGmailConnected,
    }),
    [profile, loading, updating, error, refreshProfile, updateBasicInfo, updateOptionalFields, markDataImported, setGmailConnected]
  );
}
